// Sub‑reducer for the `visitScheduleStep` slice (normalized).
// Manages calendarVisitSchedule and calendarDailySchedules as normalized collections.
import { USER_TYPE } from 'app/config/users';
import {
  ACTION_TYPES,
  getAsyncActionTypeFulfiled,
  getAsyncActionTypePending,
  getAsyncActionTypeReject
} from 'app/redux/features/pad-initialization/actions/padInitialization.types';
import STATUS from 'app/redux/features/pad-initialization/constants/status.constant.js';
import { createCalendarVisitSchedule, createEvent } from './calendarVisitSchedule.schema.js';
import {
  computeUpdateSchedulePrescriptionState,
  filterAndTransformPadVisitsToCalendarVisitSchedule,
  normalizeVisitPrescriptionSchedule,
  transformPadVisitToCalendarVisitSchedule
} from 'app/redux/features/pad-initialization/reducer/visit-schedule-step/visitScheduleStep.transform';
import {
  createVisitScheduleItem,
  transformPrescriptionScheduleToLoadSchedule
} from './visitScheduleStep.utils';
import {
  normalizeVisitPrescriptionScheduleFromObject,
  shouldIncludeAsCalendarVisitScheduleCooperative,
  shouldIncludeAsVisitPrescriptionScheduleCooperative
} from './visitScheduleStep.transform.js';

// utils
import DateFns from 'date-fns';

export const VISIT_SCHEDULE_KEYS = {
  CALENDAR: 'calendarVisitSchedule',
  DAILY: 'calendarDailySchedules',
  PRESCRIPTION: 'visitPrescriptionSchedules'
};
export const NORMALIZED_KEYS = {
  IDS: 'ids',
  BY_PAD_VISIT_ID: 'byPadVisitId'
};

const initialState = () => ({
  [VISIT_SCHEDULE_KEYS.CALENDAR]: {
    [NORMALIZED_KEYS.IDS]: [],
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {},
    status: STATUS.IDLE,
    error: null
  },
  [VISIT_SCHEDULE_KEYS.DAILY]: {
    [NORMALIZED_KEYS.IDS]: [],
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {},
    status: STATUS.IDLE,
    error: null
  },
  [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
    [NORMALIZED_KEYS.IDS]: [],
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {},
    status: STATUS.IDLE,
    error: null
  }
});

/**
 * Normalizes an array of CalendarVisitSchedule objects into a normalized map keyed by padVisitId.
 */
const normalizeCalendarVisitSchedule = calendarVisitScheduleArray => {
  const byPadVisitId = {};
  calendarVisitScheduleArray.forEach(gv => {
    const id = gv.padVisit && gv.padVisit.id ? gv.padVisit.id : null;
    if (id) {
      byPadVisitId[id] = createCalendarVisitSchedule(gv);
    }
  });
  return {
    [NORMALIZED_KEYS.IDS]: Object.keys(byPadVisitId).map(Number),
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: byPadVisitId
  };
};

const reducer = (state = initialState(), action) => {
  switch (action.type) {
    case ACTION_TYPES.SELECT_AND_UPDATE_DATA: {
      const { events, padVisit } = action.payload;
      const padVisitId = padVisit.id;

      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID][padVisitId];
      const nEvents = events.map(event => createEvent(event));

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [padVisitId]: {
              ...currentSchedule,
              events: [...nEvents]
            }
          }
        }
      };
    }
    case ACTION_TYPES.APPLY_SELECTION_VISIT_PRESCRIPTION_SCHEDULE: {
      const { events, padVisit } = action.payload;
      const padVisitId = padVisit.id;

      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.PRESCRIPTION][NORMALIZED_KEYS.BY_PAD_VISIT_ID][padVisitId];

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [padVisitId]: {
              ...currentSchedule,
              events: [...events]
            }
          }
        }
      };
    }
    case ACTION_TYPES.LOAD_VISIT_PRESCRIPTION_SCHEDULE: {
      const { schedulePrescriptionsArray, padVisitId, pad } = action.payload;

      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.PRESCRIPTION][NORMALIZED_KEYS.BY_PAD_VISIT_ID][padVisitId];

      // Get existing schedule to preserve keys
      const existingSchedule = (currentSchedule && currentSchedule.prescriptionLoadSchedule) || [];

      // Transform raw prescriptions into load schedule format using pure utility
      const transformedSchedule = transformPrescriptionScheduleToLoadSchedule(
        schedulePrescriptionsArray,
        pad,
        existingSchedule
      );

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [padVisitId]: {
              ...currentSchedule,
              prescriptionLoadSchedule: [...transformedSchedule]
            }
          }
        }
      };
    }
    case ACTION_TYPES.AUTOMATIC_REPETITION: {
      const padVisit = action.payload;
      const id = padVisit.id;
      if (!id) return state;
      const current = state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID][id];
      if (!current) return state;
      const toggled = !current.automaticRepeat;

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [id]: {
              ...current,
              automaticRepeat: toggled,
              dailyAutomaticRepeat: toggled ? false : current.dailyAutomaticRepeat
            }
          }
        }
      };
    }
    case ACTION_TYPES.DAILY_AUTOMATIC_REPETITION: {
      const padVisit = action.payload;
      const id = padVisit.id;
      if (!id) return state;
      const current = state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID][id];
      if (!current) return state;
      const toggled = !current.dailyAutomaticRepeat;

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [id]: {
              ...current,
              dailyAutomaticRepeat: toggled,
              automaticRepeat: toggled ? false : current.automaticRepeat
            }
          }
        }
      };
    }
    case ACTION_TYPES.CLEAR_SCHEDULE: {
      const padVisit = action.payload;
      const id = padVisit.id;
      if (!id) return state;
      const current = state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID][id];
      if (!current) return state;

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.CALENDAR][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [id]: {
              ...current,
              automaticRepeat: false,
              dailyAutomaticRepeat: false,
              events: []
            }
          }
        }
      };
    }
    case ACTION_TYPES.SET_CAN_STEP_FORWARD: {
      const { padVisitId, canStepFoward, sliceKey } = action.payload;
      if (!padVisitId || !sliceKey) return state;
      const slice = state[sliceKey];
      if (!slice) return state;
      const currentSchedule = slice[NORMALIZED_KEYS.BY_PAD_VISIT_ID][padVisitId];
      if (!currentSchedule) return state;

      return {
        ...state,
        [sliceKey]: {
          ...slice,
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...slice[NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [padVisitId]: {
              ...currentSchedule,
              canStepFoward: canStepFoward
            }
          }
        }
      };
    }
    /**
     * FormVisits structure (nested normalization):
     * {
     *   ids: [formCode1, formCode2, ...],
     *   byFormVisitCode: {
     *     [formCode]: { code, professionalId, startDate, workSchedule, period, workShift }
     *   }
     * }
     * This nested structure is used specifically for form data within daily schedules.
     */
    case ACTION_TYPES.DAILY_SCHEDULE_FORM_CHANGE: {
      const { padVisit, dailyScheduleForm } = action.payload;
      const scheduleId = padVisit.id;
      if (!scheduleId) return state;
      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.BY_PAD_VISIT_ID][scheduleId];
      if (!currentSchedule) return state;
      const formCode = dailyScheduleForm.code;
      if (formCode === undefined) return state;
      const existingForm = currentSchedule.formVisits.byFormVisitCode[formCode];
      if (!existingForm) return state;
      const updatedForm = { ...existingForm, ...dailyScheduleForm };

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [scheduleId]: {
              ...currentSchedule,
              formVisits: {
                ...currentSchedule.formVisits,
                byFormVisitCode: {
                  ...currentSchedule.formVisits.byFormVisitCode,
                  [formCode]: updatedForm
                }
              }
            }
          }
        }
      };
    }
    case ACTION_TYPES.DAILY_SCHEDULE_FORM_ADD: {
      const padVisit = action.payload;
      const scheduleId = padVisit.id;
      if (!scheduleId) return state;
      const currentSchedule =
        state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.BY_PAD_VISIT_ID][scheduleId];
      if (!currentSchedule) return state;
      const newForm = {
        code: Date.now(),
        professionalId: null,
        startDate: null,
        workSchedule: null,
        period: null,
        workShift: null
      };
      const newFormCode = newForm.code;
      const scheduleIds = state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.IDS].includes(scheduleId)
        ? state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.IDS]
        : [...state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.IDS], scheduleId];

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          [NORMALIZED_KEYS.IDS]: scheduleIds,
          [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: {
            ...state[VISIT_SCHEDULE_KEYS.DAILY][NORMALIZED_KEYS.BY_PAD_VISIT_ID],
            [scheduleId]: {
              ...currentSchedule,
              canStepFoward: false,
              formVisits: {
                ids: [...currentSchedule.formVisits.ids, newFormCode],
                byFormVisitCode: {
                  ...currentSchedule.formVisits.byFormVisitCode,
                  [newFormCode]: newForm
                }
              }
            }
          }
        }
      };
    }
    case ACTION_TYPES.UPDATE_SCHEDULE_PRESCRIPTION: {
      const {
        calendarDailySchedules,
        calendarVisitSchedule,
        visitPrescriptionSchedules
        //
      } = computeUpdateSchedulePrescriptionState(state, action.payload);

      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: { ...calendarVisitSchedule },
        [VISIT_SCHEDULE_KEYS.DAILY]: { ...calendarDailySchedules },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: { ...visitPrescriptionSchedules }
      };
    }
    case getAsyncActionTypeFulfiled(ACTION_TYPES.FETCH_PAD_DATA): {
      const pad = action.payload;
      const padVisits = pad.padVisits || [];
      const prescriptions = pad.padPrescriptions || [];

      // Transform using centralized business logic
      const calendarVisitScheduleArray = filterAndTransformPadVisitsToCalendarVisitSchedule(
        padVisits,
        {
          userType: USER_TYPE.HOMECARE // Context: fetching PAD data for client
        }
      );

      return {
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...normalizeCalendarVisitSchedule(calendarVisitScheduleArray),
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          status: STATUS.SUCCEEDED,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...normalizeVisitPrescriptionSchedule(prescriptions),
          status: STATUS.SUCCEEDED,
          error: null
        }
      };
    }
    case getAsyncActionTypeFulfiled(ACTION_TYPES.FETCH_PAD_VISIT_DATA): {
      const padVisit = action.payload;

      const isCalendarVisitSchedule = shouldIncludeAsCalendarVisitScheduleCooperative(padVisit);
      const isVisitPrescriptionSchedule =
        shouldIncludeAsVisitPrescriptionScheduleCooperative(padVisit);

      // TODO DÉBITO TÉCNICO: REFATORAR A FIM DE MANTER REGRAS DE NEGÓCIO FORA DO ARQUIVO DE REDUCER.
      if (isCalendarVisitSchedule) {
        //
        const calendarVisitScheduleArray = [transformPadVisitToCalendarVisitSchedule(padVisit)];

        return {
          [VISIT_SCHEDULE_KEYS.CALENDAR]: normalizeCalendarVisitSchedule(
            calendarVisitScheduleArray
          ),
          [VISIT_SCHEDULE_KEYS.DAILY]: state[VISIT_SCHEDULE_KEYS.DAILY],
          [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: state[VISIT_SCHEDULE_KEYS.PRESCRIPTION]
        };
        //
      } else if (isVisitPrescriptionSchedule) {
        // Transform using centralized business logic
        const visitPrescriptionSchedule = transformPadVisitToCalendarVisitSchedule(padVisit);

        const { padPrescriptions = [] } = padVisit;

        // const transformedSchedule = [];
        const seenSchedules = new Set();

        let transformedSchedule = [];

        padPrescriptions.forEach(padPrescription => {
          const { id: padPrescriptionId, prescriptionContent = {} } = padPrescription;
          const { prescriptionCalendars = [] } = prescriptionContent;

          prescriptionCalendars
            .filter(({ administrateDate }) => DateFns.isValid(new Date(administrateDate)))
            .filter(({ administrateVisitationId }) => !administrateVisitationId)
            .forEach((prescriptionCalendar, index) => {
              const { administrateDate } = prescriptionCalendar;

              const administrateDateDateTime = new Date(administrateDate);

              const isAdministrateDateDateTimeValid = DateFns.isValid(administrateDateDateTime);

              if (isAdministrateDateDateTimeValid) {
                const date = formatDateToDDMMYYYY(administrateDateDateTime);
                const time = formatTimeToHHMM(administrateDateDateTime);

                const uniqueVisitKey = `${date}-${time}`;

                if (!seenSchedules.has(uniqueVisitKey)) {
                  seenSchedules.add(uniqueVisitKey);

                  const dtEvent = { date, time };
                  transformedSchedule.push(
                    createVisitScheduleItem(padPrescription, dtEvent, index)
                  );
                } else {
                  transformedSchedule = [...transformedSchedule].map(schedule => {
                    if (schedule.uniqueVisitKey === uniqueVisitKey) {
                      return {
                        ...schedule,
                        padPrescriptionIds: [...schedule.padPrescriptionIds, padPrescriptionId]
                      };
                    }

                    return schedule;
                  });
                }
              }
            });
        });

        visitPrescriptionSchedule.prescriptionLoadSchedule = transformedSchedule;

        const normalizedVisitPrescription =
          normalizeVisitPrescriptionScheduleFromObject(visitPrescriptionSchedule);

        return {
          [VISIT_SCHEDULE_KEYS.CALENDAR]: state[VISIT_SCHEDULE_KEYS.CALENDAR],
          [VISIT_SCHEDULE_KEYS.DAILY]: state[VISIT_SCHEDULE_KEYS.DAILY],
          [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: normalizedVisitPrescription
        };
      }

      return state;
    }
    case getAsyncActionTypePending(ACTION_TYPES.FETCH_PAD_DATA): {
      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          status: STATUS.LOADING,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          status: STATUS.LOADING,
          error: null
        },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
          status: STATUS.LOADING,
          error: null
        }
      };
    }

    case getAsyncActionTypeReject(ACTION_TYPES.FETCH_PAD_DATA): {
      return {
        ...state,
        [VISIT_SCHEDULE_KEYS.CALENDAR]: {
          ...state[VISIT_SCHEDULE_KEYS.CALENDAR],
          status: STATUS.FAILED,
          error: action.error
        },
        [VISIT_SCHEDULE_KEYS.DAILY]: {
          ...state[VISIT_SCHEDULE_KEYS.DAILY],
          status: STATUS.FAILED,
          error: action.error
        },
        [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: {
          ...state[VISIT_SCHEDULE_KEYS.PRESCRIPTION],
          status: STATUS.FAILED,
          error: action.error
        }
      };
    }

    case ACTION_TYPES.RESET: {
      return initialState();
    }
    default:
      return state;
  }
};

export default reducer;

/**
 * Formata uma data para o padrão DD/MM/YYYY
 * @param {Date} date - Objeto Date
 * @returns {string} - Data formatada
 */
const formatDateToDDMMYYYY = date => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Formata uma data para o padrão HH:mm
 * @param {Date} date - Objeto Date
 * @returns {string} - Hora formatada
 */
const formatTimeToHHMM = date => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};
